import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box, ButtonBase, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import cx from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { chartTokens, tokens } from "../../../theme";
import ChartCard from "../cards/ChartCard";
import { shouldPinEdgeAxisLabels } from "./chartAxisLabels";
import ShChart from "./ShChart";
import { ChartError } from "./Status/ChartError";
import { NoChartData } from "./Status/NoChartData";

// Single static dataset for builder previews. A preview only renders a tinted skeleton, so
// there's no point recomputing/sorting/cloning a series per render — every preview reuses this.
const PREVIEW_SERIES = [{ name: "Preview", data: [42, 28, 35, 19, 47, 23, 31, 15] }];

export const MultiChartContent = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const labels = ['Shipped','Picked','Queued'];

    const colorType = theme.palette.mode === "dark" ? "light" : "st"; 


    // const values = props.isLoading ? [{name: "Loading",type:'bar', data: new Array(8).fill(50)}] : 
    //             props.isError ? [{name: "Error", type:'bar', data: new Array(8).fill(50)}] : props.values;
    const values = useMemo(() => {
        if (props.isLoading) return [{ name: "Loading", type: "bar", data: new Array(8).fill(50) }];
        if (props.isError) return [{ name: "Error", type: "bar", data: new Array(8).fill(50) }];
        if (!props.values) return [];
        
        return props.values.map(s => ({
            ...s,
            type: s.type || "bar"
        }));
    }, [props.values, props.isLoading, props.isError]);

    const categories = props.isLoading || props.isError ? new Array(values.length).fill('') : props.categories;

    const allValuesAreZero = useMemo(() => {
        return values?.every(item => item.data?.every(value => value === 0)) ?? true;
    }, [values]);

    const getColor = () => {
        if(typeof props.color === "undefined") return colors.txt["secondary"];
        if(props.color !== "default") return chartTokens.text.primary;
        return colors.txt["secondary"];
    }

    // A caller can hand in a formatter per series, so a revenue bar reads as money and the line
    // beside it as a count. The labels, the axis and the tooltip all run their values through it;
    // without one the value is printed as it comes.
    const formatValue = (value, seriesIndex) => {
        const formatter = props.valueFormatters?.[seriesIndex];
        return formatter ? formatter(value) : value;
    };

    const series = !allValuesAreZero ? values : [{name:"Zero",data:new Array(categories.length).fill(50)}];

    const getChartColors = () => {
        if (props.isLoading || allValuesAreZero) {
            // Use the loading color from DonutChart
            return [theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]];
        }
        if(props.isError) {
            return [colors.red[100]]
        }
        if(props.staticColors) {
            return props.staticColors;
        }
        if(props.stacked) {
            let colors = [];

            var keys = Object.keys(chartTokens[props.colors[0]][colorType]);
            for (var i = keys.length - 1; i >= keys.length - values.length; i--) {
                colors.push(chartTokens[props.colors[0]][colorType][keys[i]]);
            }
            return colors;

        }
        return props.colors.filter(function(item, pos) {
            return props.colors.indexOf(item) == pos;
        }).map((color) => chartTokens[color][colorType][700]);
    }

    return (
        <Box 
            sx={{
                width:1,
                flex:1,
                paddingTop:1.5,
                position:"relative",
                minHeight:props.minHeight ?? 300,
                "& .apexcharts-canvas" : {
                    overflow:"hidden",
                    position:"relative",
                    zIndex:3,
                    // position:"unset",
                    "& svg" : {
                        marginLeft:props.horizontal ? "-8px" : "-12px",
                        // marginLeft:props.isModal ? "-16px" : "-37px",
                        marginTop:"-16px",
                        position:"aboslute",
                    },
                    "& .apexcharts-toolbar" : {
                        // right:"50% !important",
                        right:"0px !important",
                        top:"-28px !important",
                        "@media screen and (max-width: 64em)" : {
                            display:"none"
                        }
                        // transform:"translateX(50%)"
                    },
                    "& .apexcharts-tooltip, .apexcharts-xaxistooltip, .apexcharts-yaxistooltip" : {
                        background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                        color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.white[400],
                        border:"none",
                        "& .apexcharts-tooltip-title" : {
                            display:"none"
                        }
                    }
                },
                // A long label — an amount of money — needs the height above the bar rather than
                // the width of it, or neighbouring labels run into each other as soon as the
                // chart narrows. ApexCharts has no rotation of its own for these, so the text is
                // swung up around the point it already sits on: the foot of the label stays put
                // above its bar and the rest leans away, the way rotated axis labels do.
                ...(props.dataLabelAngle ? {
                    "& .apexcharts-datalabels text" : {
                        transformBox: "fill-box",
                        // The foot of the first character is the pivot, and the label is anchored
                        // to start there, so the whole value climbs away from the bar. Pivoting
                        // on the middle instead would swing the front half back down over it.
                        transformOrigin: "0% 100%",
                        transform: `rotate(${props.dataLabelAngle}deg)`,
                    }
                } : {}),
                "@media screen and (max-width: 47.9375em)" : {
                    paddingRight:props.horizontal ? 2 : null
                },
                ...props.sx?.box
            }}
        >
            <Chart
                id={props.id ?? null} 
                width={props.width ?? "100%"}
                height={props.height ?? 6*48}
                labels={labels}
                series={series}
                options={{
                    colors: getChartColors(),
                    chart: {
                        // type:"bar",
                        stacked:props.stacked ? true : false,
                        animations: {
                            enabled:!props.isModal ??false
                        }
                    },
                    // A bar carries no stroke and a line is drawn at 4. A series that sets its
                    // own strokeWidth to 0 is still read in the tooltip but never drawn, which is
                    // how a figure rides along with the bars without a line cutting through them.
                    stroke: {
                        show: true,
                        width: series.map(serie => serie.type === "bar" ? 0 : (serie.strokeWidth ?? 4)),
                        colors: getChartColors()
                    },
                    tooltip: {
                        enabled: true,
                        shared: true,         
                        followCursor: true,    
                        intersect: false,       
                        y: {
                            formatter: function(value) {
                                return value; 
                            }
                        },
                        style: {
                            fontSize: '12px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                        },
                        custom: function({ series, seriesIndex, dataPointIndex, w }) {
                            const category = w.globals.labels[dataPointIndex];

                            // Build a list of all series for this point
                            let tooltipHTML = `<div class="apex-tooltip-custom" style='padding:8px 12px;margin-left:8px;'>`;


                            series.forEach((s, i) => {
                                const value = s[dataPointIndex];

                                if (value !== undefined) {
                                    tooltipHTML += `${w.globals.seriesNames[i]}: <b>${formatValue(value, i)}</b><br/>`;
                                }
                            });

                            tooltipHTML += '</div>';
                            return tooltipHTML;
                        }
                    },
                    plotOptions: props.plotOptions ? JSON.parse(JSON.stringify(props.plotOptions)) : undefined,
                    grid:{
                        show:false,
                        // A label swung upright stands well above its bar, so the plot gives up
                        // some height to make room for it rather than letting the tallest one run
                        // off the top of the chart.
                        ...(props.dataLabelAngle ? { padding: { top: 72 } } : {}),
                    },
                    dataLabels: {
                        background: {
                            enabled: false,  
                        },
                        enabled: true,
                        // An upright label reads away from its bar from the first character on,
                        // so it is anchored there rather than centred on the bar.
                        textAnchor: props.dataLabelAngle ? "start" : "middle",
                        formatter: function (val, opts) {
                            const seriesType = opts.w.config.series[opts.seriesIndex].type;

                            // Only show labels for bars
                            if (seriesType === "bar") {
                                return val === 0 ? "" : formatValue(val, opts.seriesIndex);
                            }

                            // Hide labels for lines
                            return "";
                        },
                        offsetY: -12,
                        style: {
                            fontSize: "12px",
                            colors: [colors.txt["primary"]],
                        },
                    },
                    xaxis: {
                        axisBorder: {
                            show: false,
                        },
                        categories: categories,
                        labels: {
                            show: !props.isLoading,
                            // offsetX: 0,
                            style: {
                                colors:getColor(),
                                fontSize: '12px',
                                fontWeight: 500,
                            },
                        },
                    },
                    // Two series on one scale squash whichever counts smaller, so a caller can
                    // ask for the rest to be measured on their own hidden axis: the shapes stay
                    // readable against each other without a second set of numbers to read.
                    yaxis: props.secondaryAxis && series.length > 1
                        ? series.map((serie, index) => ({
                            opposite: index > 0,
                            show: index === 0,
                            labels: {
                                offsetY:2,
                                offsetX:-4,
                                formatter: (value) => formatValue(value, index),
                                style: {
                                    colors:getColor(),
                                    fontSize: '12px',
                                    fontWeight: 500,
                                },
                            },
                        }))
                        : {
                            labels: {
                                offsetY:2,
                                offsetX:-4,
                                formatter: (value) => formatValue(value, 0),
                                style: {
                                    colors:getColor(),
                                    fontSize: '12px',
                                    fontWeight: 500,
                                },
                            },
                        },
                    legend:{
                        show:false,
                    },
                }} 
            />

            {allValuesAreZero && <NoChartData/>}
            {props.isError && <ChartError onRetry={props.onRetry}/>}
            
        </Box>
    );
}

export const HorizontalBarChartContentReports = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {i18n,t} = useTranslation();

    const labels = ['Shipped','Picked','Queued'];
    const colorType = theme.palette.mode === "dark" ? "light" : "st"; 

    const values = props.isLoading 
        ? [{ name: "Loading", data: new Array(8).fill(50) }] 
        : props.isError 
            ? [{ name: "Error", data: new Array(8).fill(50) }] 
            : props.values ?? []; // fallback

    // Ensure categories always exist
    const categories = props.isLoading || props.isError 
        ? new Array(values[0]?.data.length || 8).fill('') 
        : props.categories ?? [];

    // Detect if all values are zero
    const allValuesAreZero = useMemo(() => {
        if (!values || values.length === 0) return true;
        return values.every(item => item?.data?.every(value => value === 0) ?? true);
    }, [values]);


    const { sortedValues, sortedCategories } = useMemo(() => {
        if(!props.useSort) {
            return { sortedValues: values, sortedCategories: categories };
        }

        if (!values || !values.length || !categories || !categories.length) {
            return { sortedValues: values || [], sortedCategories: categories || [] };
        }

        // Check if all series have valid data arrays
        const hasValidData = values.every(series => Array.isArray(series?.data));
        if (!hasValidData) {
            return { sortedValues: values, sortedCategories: categories };
        }

        // 1. Compute total per category (across all series)
        const totals = categories.map((_, index) =>
            values.reduce((sum, series) => sum + (series?.data?.[index] ?? 0), 0)
        );

        // 2. Create sorted index map (high → low)
        const sortedIndexes = totals
            .map((total, index) => ({ total, index }))
            .sort((a, b) => b.total - a.total)
            .map(item => item.index);

        // 3. Reorder categories
        const sortedCategories = sortedIndexes.map(i => categories[i]);

        // 4. Reorder each series' data
        const sortedValues = values.map(series => ({
            ...series,
            data: sortedIndexes.map(i => series?.data?.[i] ?? 0)
        }));

        return { sortedValues, sortedCategories };
    }, [values, categories, props.useSort]);


    // If all values are zero, provide a visible fallback
    // Deep clone series to prevent ApexCharts from mutating frozen objects (e.g., from Redux/RTK Query)
    const series = useMemo(() => {
        // Builder preview only shows a tinted skeleton — reuse the one static dataset instead of
        // recomputing/sorting/cloning the real series each render.
        if (props.preview) return PREVIEW_SERIES;
        const rawSeries = !allValuesAreZero
            ? (sortedValues && sortedValues.length > 0 ? sortedValues : [{ name: "Empty", data: [0] }])
            : [{ name: "Empty", data: new Array(Math.max(sortedCategories?.length || categories?.length || 1, 1)).fill(10) }];
        return JSON.parse(JSON.stringify(rawSeries));
    }, [props.preview, allValuesAreZero, sortedValues, sortedCategories?.length, categories?.length]);

    // Memoize color functions to prevent recalculation on every render
    const labelColor = useMemo(() => {
        return theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"];
    }, [theme.palette.mode, colors.txt]);

    const chartColors = useMemo(() => {
        if (props.isLoading || allValuesAreZero) {
            // A dashboard-builder preview tints the placeholder in the flat preview color; otherwise plain grey.
            return [props.preview ? colors.purple[400] : (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100])];
        }
        if(props.isError) {
            return [colors.red[100]];
        }
        if(props.staticColors) {
            return props.staticColors;
        }
        if(props.stacked) {
            let colorArray = [];

            if (props.colors && props.colors[0] && chartTokens[props.colors[0]]) {
                const keys = Object.keys(chartTokens[props.colors[0]][colorType]);
                for (let i = keys.length - 1; i >= keys.length - values.length; i--) {
                    colorArray.push(chartTokens[props.colors[0]][colorType][keys[i]]);
                }
            }
            return colorArray.length > 0 ? colorArray : [theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]];
        }
        if (!props.colors || props.colors.length === 0) {
            return [theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]];
        }
        // Deduplicate colors more efficiently
        const uniqueColors = [...new Set(props.colors)];
        return uniqueColors.map((color) => chartTokens[color]?.[colorType]?.[700] ?? colors.grey[300]);
    }, [props.isLoading, props.preview, allValuesAreZero, props.isError, props.staticColors, props.stacked, props.colors, theme.palette.mode, colors, colorType, values.length]);

    // Calculate initial width to prevent small render flash.
    // A numeric `width` prop overrides the per-category estimate for callers that size the bars themselves.
    const initialWidth = useMemo(() => {
        if (typeof props.width === "number") return props.width || 800;
        return props.categories?.length * (24 + (props.values?.length || 0) * 18) || 800;
    }, [props.width, props.categories?.length, props.values?.length]);

    const [chartWidth, setChartWidth] = useState(initialWidth);
    const [showScrollButton, setShowScrollButton] = useState(false);

    const containerRef = useRef(null);
    const [chartMode, setChartMode] = useState('pan');

    // Deferred chart rendering to prevent UI freeze
    const [shouldRenderChart, setShouldRenderChart] = useState(false);
    const chartDataKey = useMemo(() => {
        return `${props.categories?.length}-${props.values?.length}-${allValuesAreZero}`;
    }, [props.categories?.length, props.values?.length, allValuesAreZero]);

    useEffect(() => {
        // Reset render state when data changes
        setShouldRenderChart(false);

        // Defer chart rendering to next frame to keep UI responsive
        const timeoutId = setTimeout(() => {
            setShouldRenderChart(true);
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [chartDataKey]);

    useEffect(() => {
        if (!containerRef.current) return;

        const updateWidth = () => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const calculatedWidth = typeof props.width === "number"
                ? props.width
                : props.categories?.length * (24 + props.values?.length * 18);
            setChartWidth(Math.max(rect.width, calculatedWidth));

            setShowScrollButton(calculatedWidth > rect.width);
        };

        // run once initially
        updateWidth();

        // Debounced resize handler
        let resizeTimer;
        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(updateWidth, 100);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            clearTimeout(resizeTimer);
        };
    }, [props.isError,props.isLoading,props.width,props.categories?.length, props.values?.length]);

    // Scroll right when button clicked
    const handleScrollRight = (e) => {
        e.stopPropagation();
        if (containerRef.current) {
            containerRef.current.scrollBy({ left: 300, behavior: "smooth" });
        }
    };

    const handleScrollLeft = (e) => {
        e.stopPropagation();
        if (containerRef.current) {
            containerRef.current.scrollBy({ left: -300, behavior: "smooth" });
        }
    };


    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    const onMouseDown = (e) => {
        isDragging.current = true;
        startX.current = e.pageX - containerRef.current.offsetLeft;
        scrollLeft.current = containerRef.current.scrollLeft;
    };

    const onMouseMove = (e) => {
        if (!isDragging.current) return;
        e.preventDefault();
        const x = e.pageX - containerRef.current.offsetLeft;
        const walk = x - startX.current;
        containerRef.current.scrollLeft = scrollLeft.current - walk;
    };

    const onMouseUp = () => {
        isDragging.current = false;
    };

    const onMouseLeave = () => {
        isDragging.current = false;
    };


    // Memoize tick positioning with useCallback
    const setTickPositions = useCallback(() => {
        setTimeout(() => {
            if(containerRef.current && shouldPinEdgeAxisLabels(containerRef.current)) {
                const p = containerRef.current.querySelectorAll(".apexcharts-xaxis-texts-g")[0];
                const ticks = containerRef.current.querySelectorAll(".apexcharts-xaxis-tick");
                const lastTickPos = ticks[ticks.length-1]?.getAttribute('x2');
                if(typeof p !== "undefined"){
                    p.childNodes[0]?.setAttribute('x', 10);
                    p.childNodes[p.childNodes.length-1]?.setAttribute('x', lastTickPos-12);
                }
            }
        }, 20);
    }, []);

    useEffect(() => {
        setTickPositions();
    },[theme.palette.mode, setTickPositions]);

    // Add states for button visibility
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(false);

    // Memoize update function to prevent recreation
    const updateScrollButtons = useCallback(() => {
        if (containerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
            const hasOverflow = scrollWidth > clientWidth;

            setShowLeftButton(hasOverflow && scrollLeft > 24);
            setShowRightButton(hasOverflow && scrollLeft < (scrollWidth - clientWidth));
        }
    }, []);

    // Consolidated resize and scroll event listeners
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Debounced resize handler
        let resizeTimer;
        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                setTickPositions();
                updateScrollButtons();
            }, 100);
        };

        // Scroll handler
        container.addEventListener('scroll', updateScrollButtons);
        window.addEventListener('resize', handleResize);

        return () => {
            container.removeEventListener('scroll', updateScrollButtons);
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimer);
        };
    }, [updateScrollButtons, setTickPositions]);

    // Update scroll buttons when chartWidth changes
    useEffect(() => {
        updateScrollButtons();
    }, [chartWidth, updateScrollButtons]);

    // Memoize tooltip formatter to prevent recreation on every render
    const tooltipFormatter = useCallback(({ series, dataPointIndex, w }) => {
        const category = w.globals.labels[dataPointIndex];
        const formatterFn = props.chartSettings?.chart?.labelFormat?.dataLabels;

        let tooltipHtml = `
            <div class="apex-tooltip-custom" style="padding:8px 12px;">
                <div style="margin-bottom:6px;font-weight:600;">
                ${category}
                </div>
        `;

        w.globals.seriesNames.forEach((name, i) => {
            let val = series[i][dataPointIndex];

            // Hide exact zero
            if (val === 0) val = 0;

            // Hide values that would round to 0 (e.g. 0.001 → €0.00)
            if (Math.round(val * 100) / 100 === 0) val = 0;

            if (typeof formatterFn === 'function') {
                val = formatterFn(i18n, val);
            }

            if (val !== undefined) {
                tooltipHtml += `
                    <div style="display:flex;align-items:center;margin:2px 0;">
                        <span
                            style="
                                width:10px;
                                height:10px;
                                background:${w.globals.colors[i]};
                                display:inline-block;
                                margin-right:6px;
                                border-radius:5px;
                            "
                        >
                        </span>
                        <span>${t(name)}: <b>${val}</b></span>
                    </div>
                `;
            }
        });

        tooltipHtml += `</div>`;
        return tooltipHtml;
    }, [props.chartSettings?.chart?.labelFormat?.dataLabels, i18n, t]);

    // Memoize data label formatter
    const seriesCount = series?.length || 1;
    const dataLabelFormatter = useCallback((val) => {
        const formatterFn = props.chartSettings?.chart?.labelFormat?.dataLabels;

        // Hide exact zero
        if (val === 0) return '';

        // Hide values that would round to 0
        if (Math.round(val * 100) / 100 === 0) return '';

        let formattedValue = val;
        if (typeof formatterFn === 'function') {
            formattedValue = formatterFn(i18n, val);
        }

        return formattedValue;
    }, [props.chartSettings?.chart?.labelFormat?.dataLabels, i18n]);

    // Post-render adjustment to stagger overlapping data labels vertically
    const adjustOverlappingLabels = useCallback(() => {
        if (!containerRef.current || seriesCount <= 1) return;

        // Collect all data labels with their positions and dimensions
        const allLabels = [];
        const labelGroups = containerRef.current.querySelectorAll('.apexcharts-data-labels');

        labelGroups.forEach((group, seriesIdx) => {
            const texts = group.querySelectorAll('text');
            texts.forEach((text) => {
                const x = parseFloat(text.getAttribute('x')) || 0;
                const y = parseFloat(text.getAttribute('y')) || 0;
                const content = text.textContent || '';
                if (content.trim()) {
                    // Get actual text width using getBBox
                    let width = 50; // default estimate
                    try {
                        const bbox = text.getBBox();
                        width = bbox.width;
                    } catch (e) {
                        // getBBox can fail if element is not rendered
                    }
                    allLabels.push({ text, seriesIdx, x, y, width, originalY: y });
                }
            });
        });

        if (allLabels.length === 0) return;

        // Sort labels by x position, then by y position (to process top to bottom)
        allLabels.sort((a, b) => a.x - b.x || a.y - b.y);

        // Check each label against all others for overlap using actual bounding boxes
        for (let i = 0; i < allLabels.length; i++) {
            const current = allLabels[i];
            const currentLeft = current.x - current.width / 2;
            const currentRight = current.x + current.width / 2;

            for (let j = 0; j < i; j++) {
                const other = allLabels[j];
                const otherLeft = other.x - other.width / 2;
                const otherRight = other.x + other.width / 2;

                // Check if labels overlap horizontally (bounding boxes intersect)
                const horizontalOverlap = !(currentRight < otherLeft || currentLeft > otherRight);
                if (!horizontalOverlap) continue;

                // Check if labels overlap vertically (within 14px)
                const yDiff = Math.abs(current.y - other.y);
                if (yDiff < 14) {
                    // Move current label up to avoid overlap
                    const newY = other.y - 14;
                    current.text.setAttribute('y', newY);
                    current.y = newY;
                }
            }
        }
    }, [seriesCount]);

    // Memoize xaxis label formatter
    const xAxisFormatter = useCallback((val) => {
        if (props.chartSettings?.chart?.labelFormat?.xaxis) {
            return props.chartSettings.chart.labelFormat.xaxis(i18n, val);
        }
        return val;
    }, [props.chartSettings?.chart?.labelFormat?.xaxis, i18n]);

    // Memoize yaxis label formatter
    const yAxisFormatter = useCallback((val) => {
        if (props.chartSettings?.chart?.labelFormat?.yaxis) {
            return props.chartSettings.chart.labelFormat.yaxis(i18n, val);
        }
        return val === 0 ? '' : val;
    }, [props.chartSettings?.chart?.labelFormat?.yaxis, i18n]);

    // Memoize the entire chart options object to prevent ApexCharts re-rendering
    const chartOptions = useMemo(() => ({
        colors: chartColors,
        chart: {
            stacked: props.stacked ? true : false,
            animations: {
                enabled: !props.isModal ?? false
            },
            selection: {
                enabled: true
            },
            pan: {
                enabled: true,
            },
            toolbar: {
                autoSelected: "pan",
                tools: {
                    download: true,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true
                }
            },
        },
        stroke: {
            show: true,
            width: 4,
            colors: ['transparent'],
        },
        tooltip: {
            shared: window.innerWidth >= 768,
            intersect: window.innerWidth < 768,
            followCursor: true,
            custom: tooltipFormatter
        },
        plotOptions: props.plotOptions ? JSON.parse(JSON.stringify(props.plotOptions)) : undefined,
        legend: { show: false },
        grid: {
            show: false,
        },
        dataLabels: {
            enabled: true,
            formatter: dataLabelFormatter,
            offsetY: -20,
            style: {
                fontSize: "12px",
                colors: [colors.txt["primary"]],
            },
        },
        xaxis: {
            axisBorder: {
                show: false,
            },
            categories: sortedCategories && sortedCategories.length > 0 ? sortedCategories : [''],
            labels: {
                show: !props.isLoading,
                style: {
                    colors: labelColor,
                    fontSize: '12px',
                    fontWeight: 500,
                },
                formatter: xAxisFormatter
            },
        },
        yaxis: {
            labels: {
                offsetY: 2,
                offsetX: -4,
                style: {
                    colors: labelColor,
                    fontSize: '12px',
                    fontWeight: 500,
                },
                formatter: yAxisFormatter
            },
        },
        legend: {
            show: false,
        },
    }), [
        chartColors,
        props.stacked,
        props.isModal,
        props.plotOptions,
        props.isLoading,
        tooltipFormatter,
        dataLabelFormatter,
        xAxisFormatter,
        yAxisFormatter,
        sortedCategories,
        labelColor,
        colors.txt
    ]);

    // Adjust overlapping labels after chart renders
    useEffect(() => {
        if (!shouldRenderChart || seriesCount <= 1) return;

        // Wait for ApexCharts to finish rendering
        const timeoutId = setTimeout(() => {
            adjustOverlappingLabels();
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [shouldRenderChart, series, sortedCategories, adjustOverlappingLabels, seriesCount]);

    return (
        <Box
            sx={{
                width:1,
                position:"relative",
            }}
        >
            <Box 
                className={cx(undefined, {
                    "hide-overflow" : props.isLoading || props.isError
                })}
                ref={containerRef}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseLeave}
                sx={{
                    // width:chartWidth,
                    overflowX: 'auto',
                    // whiteSpace: 'nowrap', // for horizontal scrolling of child elements

                    // minWidth: '100%', // ensures chart always fills parent

                    // width: `${Math.max(props.categories.length * (24 + 32), 100)}px`, // dynamic width
                    // width: `max(${props.categories.length * (24 + 32)}px, 100%)`, // dynamic
                    // width: `${(24 + 32) * (props.categories?.length ?? 6)}px`, // bar width + gap
                    flex:3,
                    flexShrink:0,
                    paddingTop:1.5,
                    position:"relative",
                    cursor:"grab",
                    minHeight:props.minHeight ?? 300,
                    "& .apexcharts-canvas" : {
                        // overflow:"hidden",
                        position:"relative",
                        zIndex:3,
                        // position:"unset",
                        "& svg" : {
                            marginLeft:props.horizontal ? "-8px" : "-12px",
                            // marginLeft:props.isModal ? "-16px" : "-37px",
                            marginTop:"-16px",
                            position:"aboslute",
                        },
                        "& .apexcharts-toolbar" : {
                            // right:"50% !important",
                            right:"0px !important",
                            top:"-28px !important",
                            "@media screen and (max-width: 64em)" : {
                                display:"none"
                            }
                            // transform:"translateX(50%)"
                        },
                        "& .apexcharts-tooltip, .apexcharts-xaxistooltip, .apexcharts-yaxistooltip" : {
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.txt["primary"],
                            color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.white[400],
                            border:"none",
                            "& .apexcharts-tooltip-title" : {
                                display:"none"
                            }
                        }
                    },
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:props.horizontal ? 2 : null
                    },
                    "&.hide-overflow" : {
                        overflow:"hidden"
                    },
                    ...props.sx?.box
                }}
            >
                {!allValuesAreZero && shouldRenderChart &&
                <Chart
                    id={props.id ?? null}
                    width={chartWidth}
                    height={props.height ?? 6*48}
                    labels={labels}
                    series={series}
                    options={chartOptions}
                    type="bar"
                />
                }

                {!allValuesAreZero && !shouldRenderChart && (
                    <Box
                        sx={{
                            width: chartWidth,
                            height: props.height ?? 6*48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {/* Empty placeholder during deferred render - maintains layout */}
                    </Box>
                )}

                {allValuesAreZero && <NoChartData sx={{left:0,}} />}
                {props.isError && <ChartError sx={{left:0,borderRadius:4,}} onRetry={props.onRetry}/>}

            </Box>
            {/* {showScrollButton && (
                <ButtonBase
                    className="asdoijew9feuwbh"
                    onClick={handleScrollRight}
                    sx={{
                        zIndex:33,
                        position: "absolute",
                        right: 32,
                        top:0,
                        height:300,
                        width:32,
                        // transform: "translateY(-50%)",
                        backgroundColor: "rgba(0,0,0,0.3)",
                        color: "white",
                        "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.5)",
                        },
                    }}
                >
                    <ArrowForwardIosIcon fontSize="small" />
                </ButtonBase>
            )} */}
             {showScrollButton && showLeftButton && (!props.isLoading && !props.isError) &&(
                <ButtonBase
                    onClick={handleScrollLeft}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                        zIndex:33,
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: 290,
                        width: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius:4,
                        cursor: "pointer",
                    }}
                >
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background:alpha(theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[300], 0.5),
                            color: "#fff",
                            boxShadow: "0 2px 4px rgba(10, 7, 7, 0.2)",
                            transform:"rotate(180deg)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <ArrowForwardIosIcon fontSize="small" />
                    </Box>
                </ButtonBase>
            )}
            {showScrollButton && showRightButton && (!props.isLoading && !props.isError) && (
                <ButtonBase
                    onClick={handleScrollRight}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                        zIndex:33,
                        position: "absolute",
                        top: 0,
                        right: 0,
                        height: 290,
                        width: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius:4,
                        cursor: "pointer",
                    }}
                >
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background:alpha(theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[300], 0.5),
                            color: "#fff",
                            boxShadow: "0 2px 4px rgba(10, 7, 7, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <ArrowForwardIosIcon fontSize="small" />
                    </Box>
                </ButtonBase>
            )}
        </Box>
    );
}


export const HorizontalBarChartContent = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const labels = ['Shipped','Picked','Queued'];

    const colorType = theme.palette.mode === "dark" ? "light" : "st"; 

    const values = props.isLoading ? [{name: "Loading", data: new Array(8).fill(50)}] : 
                props.isError ? [{name: "Error", data: new Array(8).fill(50)}] : props.values;

    const categories = props.isLoading || props.isError ? new Array(values.length).fill('') : props.categories;

    const allValuesAreZero = useMemo(() => {
        return values?.every(item => item.data.every(value => value === 0)) ?? true;
    }, [values]);

    // Deep clone series to prevent ApexCharts from mutating frozen objects (e.g., from Redux/RTK Query)
    const series = useMemo(() => {
        const rawSeries = !allValuesAreZero
            ? values
            : [{name: "Zero", data: new Array(categories?.length ?? 0).fill(50)}];
        return JSON.parse(JSON.stringify(rawSeries ?? []));
    }, [allValuesAreZero, values, categories?.length]);

    const getColor = () => {
        if(typeof props.color === "undefined") return colors.txt["secondary"];
        if(props.color !== "default") return chartTokens.text.primary;
        return colors.txt["secondary"];
    }

    const getChartColors = () => {
        if (props.isLoading || allValuesAreZero) {
            // Use the loading color from DonutChart
            return [theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]];
        }
        if(props.isError) {
            return [colors.red[100]]
        }
        if(props.staticColors) {
            return props.staticColors;
        }
        if(props.stacked) {
            let colors = [];

            var keys = Object.keys(chartTokens[props.colors[0]][colorType]);
            for (var i = keys.length - 1; i >= keys.length - values.length; i--) {
                colors.push(chartTokens[props.colors[0]][colorType][keys[i]]);
            }
            return colors;

        }
        return props.colors.filter(function(item, pos) {
            return props.colors.indexOf(item) == pos;
        }).map((color) => chartTokens[color][colorType][700]);
    }

    return (
        <Box
            sx={{
                width:1,
                flex:1,
                paddingTop:1.5,
                position:"relative",
                minHeight:props.minHeight ?? 300,
                "& .apexcharts-canvas" : {
                    overflow:"hidden",
                    position:"relative",
                    zIndex:3,
                    // position:"unset",
                    "& svg" : {
                        marginLeft:props.horizontal ? "-8px" : "-12px",
                        // marginLeft:props.isModal ? "-16px" : "-37px",
                        marginTop:"-16px",
                        position:"aboslute",
                    },
                    "& .apexcharts-toolbar" : {
                        // right:"50% !important",
                        right:"0px !important",
                        top:"-28px !important",
                        "@media screen and (max-width: 64em)" : {
                            display:"none"
                        }
                        // transform:"translateX(50%)"
                    },
                    "& .apexcharts-tooltip, .apexcharts-xaxistooltip, .apexcharts-yaxistooltip" : {
                        background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                        color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.white[400],
                        border:"none",
                        "& .apexcharts-tooltip-title" : {
                            display:"none"
                        }
                    }
                },
                "@media screen and (max-width: 47.9375em)" : {
                    paddingRight:props.horizontal ? 2 : null
                },
                ...props.sx?.box
            }}
        >
            <Chart
                id={props.id ?? null}
                width={props.width ?? "100%"}
                height={props.height ?? 6*48}
                labels={labels}
                series={series}
                options={{
                    // colors : ['#b84644', '#4576b5'],
                    colors: getChartColors(),
                    // fill : {
                    //     colors: props.stacked ? [getChartColors()]: null
                    // },
                    // fill: props.stacked ? getChartColors() : null,
                    chart: {
                        stacked:props.stacked ? true : false,
                        animations: {
                            enabled:!props.isModal ??false
                        }
                    },
                    stroke: {
                    show: true,
                    width: 4, // ← controls visual "gap"
                    colors: ['transparent'], // ← match your background
                    },
                    tooltip: {
                        custom: function({ series: seriesData, seriesIndex, dataPointIndex, w }) {
                            const category = w.globals.labels[dataPointIndex]; // Get category label
                            // return `<div class="apex-tooltip-custom">${category}</div>`;
                            return `<div class="apex-tooltip-custom" style='padding:8px 12px;margin-left:8px;'>${category}: <b>${seriesData[seriesIndex][dataPointIndex]}</b></div>`;
                        },
                        // enabled:false
                    },
                    plotOptions: props.plotOptions ? JSON.parse(JSON.stringify(props.plotOptions)) : undefined,
                    grid:{
                        show:false,
                    },
                    dataLabels: {
                        enabled: props.hideDataLabels ? false : true,
                        formatter: function (val) {
                            return val === 0 ? '' : val;
                        },
                        offsetY: -20, // Adjust this to move label higher or lower
                        style: {
                        fontSize: "12px",
                        colors: [colors.txt["primary"]],
                        },
                    },
                    // dataLabels: {
                    //     enabled:false
                    // },
                    xaxis: {
                        
                        axisBorder: {
                            show: false,
                            // color: 'orange',
                            // height: 0,
                            // width: '100%',
                        },
                        categories: categories,
                        labels: {
                            show: !props.isLoading,
                            // offsetX: 0,
                            style: {
                                colors:getColor(),
                                // colors: ["blue","orange","grey"],
                                fontSize: '12px',
                                // fontFamily: 'Helvetica, Arial, sans-serif',
                                fontWeight: 500,
                                // cssClass: 'apexcharts-xaxis-label',
                            },
                        },
                    },
                    yaxis: {

                        labels: {
                            offsetY:2,
                            offsetX:-4,
                            style: {
                                colors:getColor(),
                                // colors: ["blue","orange","grey"],
                                fontSize: '12px',
                                // fontFamily: 'Helvetica, Arial, sans-serif',
                                fontWeight: 500,
                                // cssClass: 'apexcharts-xaxis-label',
                            },
                        },
                    },
                    legend:{
                        show:false,
                    },
                }} 
                type="bar"
            />

            {allValuesAreZero && <NoChartData/>}
            {props.isError && <ChartError onRetry={props.onRetry}/>}
            
        </Box>
    );
}

export const HorizontalBarChart = (props) => {
    var plotOptions = {
        bar: {
            barHeight:"50%",
            // columnHeight:"122px",
            borderRadius: 8,
            horizontal: true,
        }
    }

    return (
        <ShChart color={props.color} type={props.type}>
            <ChartCard 
                {...props}
            >
                <HorizontalBarChartContent {...props} categories={props.categories} values={props.values} horizontal plotOptions={plotOptions}/>
            </ChartCard>
        </ShChart>
    );
}

export const StackedHorizontalBarChart = (props) => {
    var plotOptions = {
        bar: {
            barHeight:"50%",
            // columnHeight:"122px",
            borderRadius: 8,
            horizontal: true,
        }
    }

    return (
        <ShChart>
            <ChartCard 
                {...props}
            >
                <HorizontalBarChartContent {...props} categories={props.categories} values={props.values} stacked horizontal plotOptions={plotOptions} />
            </ChartCard>
        </ShChart>
    );
}

export const BarChart = (props) => {
    var plotOptions = {
        bar: {
            borderRadius: props.barBorderRadius ?? 12,
            distributed:true,
            columnWidth: props.columnWidth ?? '24px',
            dataLabels: {
              position: "top", // 👈 This places labels above the bars
            },
        }
    }
    return (
        <ShChart>
            <ChartCard
                {...props}
            >
                <HorizontalBarChartContent {...props} categories={props.categories} values={props.values} color={props.color} plotOptions={plotOptions}/>
            </ChartCard>
        </ShChart>
    );
}
export const MultiChart = (props) => {
    var plotOptions = {
        bar: {
            borderRadius: 12,
            distributed:false,
            columnWidth:'24px',
            dataLabels: {
              position: "top", // 👈 This places labels above the bars
            },
        }
    }
    return (
        <ShChart>
            <ChartCard 
                {...props}
            >
                <MultiChartContent {...props} categories={props.categories} values={props.values} color={props.color} plotOptions={plotOptions}/>
            </ChartCard>
        </ShChart>
    );
}
export const StackedBarChart = (props) => {
    var plotOptions = {
        bar: {
            borderRadius: 16,
            columnWidth:'32px',
        }
    }
    return (
        <ShChart>
            <ChartCard 
                {...props}
            >
                <HorizontalBarChartContent {...props}  categories={props.categories} values={props.values} stacked color={props.color} plotOptions={plotOptions}/>
            </ChartCard>
        </ShChart>
    );
}